import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage, parseErrorStatus } from "@/lib/queryClient";
import type { LockCodeComparison, PadsplitLockProgress } from "./padsplit-sync-types";

export const LOCK_PROGRESS_INTERVAL_MS = 4_000;
export const LOCK_PROGRESS_TIMEOUT_MS = 120_000;
const REQUEST_TIMEOUT_MS = 20_000;
const COMPARISON_URL = "/api/app/devices/padsplit-locks/comparison";
const PROGRESS_URL = "/api/app/devices/padsplit-locks/progress";

export function countRefreshedLocks(baseline: PadsplitLockProgress, current: PadsplitLockProgress) {
  const timestamps = new Map(current.locks.map((lock) => [lock.id, lock.syncedAt]));
  const refreshed = baseline.locks.filter((lock) => {
    const next = timestamps.get(lock.id);
    return next != null && Number.isFinite(Date.parse(next)) &&
      (lock.syncedAt == null || Date.parse(next) > Date.parse(lock.syncedAt));
  }).length;
  return { refreshed, total: baseline.locks.length, confirmed: baseline.locks.length > 0 && refreshed === baseline.locks.length };
}

function fingerprint(snapshot: PadsplitLockProgress, includeAttemptMetadata = true) {
  return JSON.stringify({
    locks: [...snapshot.locks].sort((a, b) => a.id - b.id).map((lock) => [lock.id, lock.syncedAt]),
    pushes: [...snapshot.pushes].sort((a, b) => a.id - b.id).map((push) => [
      push.id, push.sentAt, push.sendError, push.cancelledAt,
      ...(includeAttemptMetadata ? [push.attempts, push.updatedAt] : []),
    ]),
  });
}

type HarvestPhase = "idle" | "preparing" | "awaiting" | "partial" | "confirmed" | "timeout" | "error";
interface ProgressView {
  harvest: HarvestPhase;
  refreshed: number;
  total: number;
  pendingPushes: number;
  deliveredPushes: number;
  failedPushes: number;
  cancelledPushes: number;
  pushTimedOut: boolean;
  error: string | null;
}
const EMPTY_VIEW: ProgressView = {
  harvest: "idle", refreshed: 0, total: 0, pendingPushes: 0,
  deliveredPushes: 0, failedPushes: 0, cancelledPushes: 0, pushTimedOut: false, error: null,
};
interface Observer {
  harvest: () => Promise<boolean>;
  comparison: (data: LockCodeComparison) => void;
  retryPush: (id: number) => Promise<boolean>;
  checkPushes: () => void;
}

/**
 * One bounded database observer per property/session, shared by harvest and push
 * tracking. Timers depend on the scope, not render-time arrays or callbacks.
 * Provider reads happen only when persisted data changes (or an explicit action).
 */
export function usePadsplitLockProgress(
  propertyId: number | null,
  sessionKey: string,
  comparison: LockCodeComparison | undefined,
) {
  const client = useQueryClient();
  const observer = useRef<Observer | null>(null);
  const [view, setView] = useState<ProgressView>(EMPTY_VIEW);

  useEffect(() => {
    setView(EMPTY_VIEW);
    if (propertyId == null) return;
    let active = true;
    let busy = false;
    let preparing = false;
    let baseline: PadsplitLockProgress | null = null;
    let previous: PadsplitLockProgress | null = null;
    let previousFromComparison = false;
    let harvestDeadline: number | null = null;
    let snapshot: PadsplitLockProgress | null = null;
    const postControllers = new Set<AbortController>();
    let inFlight: Promise<PadsplitLockProgress> | null = null;
    const pending = new Map<number, number>();
    const seenPushes = new Set<number>();
    const retryBaselines = new Map<number, PadsplitLockProgress["pushes"][number]>();
    const key = [PROGRESS_URL, propertyId, sessionKey] as const;
    const update = (patch: Partial<ProgressView>) => {
      if (active) setView((old) => ({ ...old, ...patch }));
    };
    const invalidate = () => {
      if (active) void client.invalidateQueries({ queryKey: [COMPARISON_URL, propertyId], exact: true });
    };
    const read = () => {
      // fetchQuery deduplicates reads; staleTime: 0 guarantees a fresh baseline.
      const request = client.fetchQuery<PadsplitLockProgress>({
        queryKey: key,
        staleTime: 0,
        retry: false,
        queryFn: async ({ signal }) => {
          const controller = new AbortController();
          const abort = () => controller.abort();
          signal.addEventListener("abort", abort, { once: true });
          const timeout = setTimeout(abort, REQUEST_TIMEOUT_MS);
          try {
            const res = await apiRequest("GET", `${PROGRESS_URL}?propertyId=${propertyId}`, undefined, { signal: controller.signal });
            const data = await res.json() as PadsplitLockProgress;
            if (data.propertyId !== propertyId || !Array.isArray(data.locks) || !Array.isArray(data.pushes)) {
              throw new Error("Invalid property-scoped lock progress response.");
            }
            return data;
          } finally {
            clearTimeout(timeout);
            signal.removeEventListener("abort", abort);
          }
        },
      });
      inFlight = request;
      return request;
    };
    const post = async (url: string, body?: unknown) => {
      const controller = new AbortController();
      postControllers.add(controller);
      const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
      try {
        await apiRequest("POST", url, body, { signal: controller.signal });
      } finally {
        clearTimeout(timeout);
        postControllers.delete(controller);
      }
    };
    const trackPending = (data: PadsplitLockProgress) => {
      for (const push of data.pushes) {
        if (!push.sentAt && !push.sendError && !push.cancelledAt && !seenPushes.has(push.id)) {
          seenPushes.add(push.id);
          pending.set(push.id, Date.now() + LOCK_PROGRESS_TIMEOUT_MS);
        }
      }
    };
    const observe = (data: PadsplitLockProgress) => {
      if (!active) return;
      if (previous) {
        // A comparison only exposes pushes overlaying child rows. Additional
        // history/attempt metadata in our first DB read is not itself a change.
        const comparable = previousFromComparison
          ? { ...data, pushes: data.pushes.filter((push) => previous!.pushes.some((old) => old.id === push.id)) }
          : data;
        if (fingerprint(previous, !previousFromComparison) !== fingerprint(comparable, !previousFromComparison)) invalidate();
      }
      previous = data;
      previousFromComparison = false;
      snapshot = data;
      trackPending(data);
      for (const push of data.pushes) {
        const retryBaseline = retryBaselines.get(push.id);
        const unchangedRetryError = retryBaseline && push.sendError === retryBaseline.sendError &&
          push.attempts === retryBaseline.attempts && push.updatedAt === retryBaseline.updatedAt;
        if (push.sentAt || push.cancelledAt || (push.sendError && !unchangedRetryError)) {
          pending.delete(push.id);
          retryBaselines.delete(push.id);
        } else if (!push.sendError) {
          // Once the old error clears, any subsequent failure is new evidence.
          retryBaselines.delete(push.id);
        }
      }
      update({
        pendingPushes: pending.size,
        deliveredPushes: data.pushes.filter((push) => push.sentAt && !push.cancelledAt).length,
        failedPushes: data.pushes.filter((push) => push.sendError && !push.sentAt && !push.cancelledAt).length,
        cancelledPushes: data.pushes.filter((push) => push.cancelledAt).length,
        error: null,
      });
      if (baseline && harvestDeadline != null) {
        const result = countRefreshedLocks(baseline, data);
        update({ refreshed: result.refreshed, total: result.total, harvest: result.confirmed ? "confirmed" : result.refreshed > 0 ? "partial" : "awaiting" });
        if (result.confirmed) harvestDeadline = null;
      }
    };
    const tick = async (force = false) => {
      if (!active) return;
      // Deadlines are checked even while a read is pending.
      if (harvestDeadline != null && Date.now() >= harvestDeadline) {
        harvestDeadline = null;
        update({ harvest: "timeout" });
      }
      for (const [id, deadline] of pending) {
        if (Date.now() >= deadline) {
          pending.delete(id);
          update({ pushTimedOut: true, pendingPushes: pending.size });
        }
      }
      if (busy || preparing || (!force && harvestDeadline == null && pending.size === 0)) return;
      busy = true;
      try {
        observe(await read());
      } catch (error) {
        update({ error: parseErrorMessage(error, "Couldn't read persisted lock progress.") });
        if ([401, 403].includes(parseErrorStatus(error) ?? 0)) {
          update({ harvest: harvestDeadline != null ? "error" : "idle", pendingPushes: 0 });
          stop();
        }
      } finally {
        busy = false;
      }
    };
    const timer = setInterval(() => { void tick(); }, LOCK_PROGRESS_INTERVAL_MS);
    const stop = () => {
      active = false;
      clearInterval(timer);
      for (const controller of postControllers) controller.abort();
      void client.cancelQueries({ queryKey: key, exact: true });
    };
    // resetSessionCache clears these queries at logout/account boundaries.
    const unsubscribe = client.getQueryCache().subscribe((event) => {
      if (event.type === "removed" && (
        event.query.queryKey[0] === "/api/app/auth/me" ||
        (event.query.queryKey[0] === COMPARISON_URL && event.query.queryKey[1] === propertyId)
      )) {
        update({ ...EMPTY_VIEW, error: "Session changed. Reopen this tab to check lock progress." });
        stop();
      }
    });
    const engine: Observer = {
      comparison(data) {
        if (!active || data.propertyId !== propertyId) return;
        const pushes = new Map<number, PadsplitLockProgress["pushes"][number]>();
        for (const lock of data.locks) {
          for (const row of lock.rows) {
            if (row.pushId != null) pushes.set(row.pushId, {
              id: row.pushId, sentAt: row.pushSentAt, sendError: row.pushError, cancelledAt: null,
            });
          }
        }
        const seed = {
          propertyId, padsplitPropertyId: data.padsplitPropertyId,
          locks: data.locks.map((lock) => ({ id: lock.padsplitLockRowId, syncedAt: lock.syncedAt })),
          pushes: [...pushes.values()],
        };
        // Only seed once: later provider/comparison responses must not overwrite
        // our independently observed DB baseline or restart completed observers.
        if (!previous) {
          previous = seed;
          previousFromComparison = true;
          snapshot = seed;
          update({
            deliveredPushes: seed.pushes.filter((push) => push.sentAt).length,
            failedPushes: seed.pushes.filter((push) => push.sendError && !push.sentAt).length,
          });
        }
        trackPending(seed);
        update({ pendingPushes: pending.size });
      },
      async harvest() {
        if (!active || preparing || harvestDeadline != null) return false;
        preparing = true;
        update({ harvest: "preparing", refreshed: 0, total: 0, error: null });
        try {
          // Do not reuse the page-load snapshot or a read begun before this click.
          await inFlight?.catch(() => undefined);
          if (!active) return false;
          const fresh = await read();
          if (!active) return false;
          observe(fresh);
          baseline = fresh;
          // HTTP success means accepted, with scheduled/no-job-ID or queued.
          await post("/api/app/padsplit/harvest/locks", { propertyId });
          if (!active) return false;
          harvestDeadline = Date.now() + LOCK_PROGRESS_TIMEOUT_MS;
          update({ harvest: "awaiting", total: fresh.locks.length });
          return true;
        } catch (error) {
          if (!active) return false;
          update({ harvest: "error", error: parseErrorMessage(error, "Couldn't start the PadSplit sync.") });
          throw error;
        } finally {
          preparing = false;
        }
      },
      async retryPush(id) {
        if (!active) return false;
        try {
          await inFlight?.catch(() => undefined);
          if (!active) return false;
          const fresh = await read();
          if (!active) return false;
          observe(fresh);
          const push = fresh.pushes.find((row) => row.id === id);
          if (!push) throw new Error("The selected push is not present in this property's current progress.");
          await post(`/api/app/devices/padsplit-locks/pushes/${id}/retry`);
          if (!active) return false;
          retryBaselines.set(id, push);
          seenPushes.add(id);
          pending.set(id, Date.now() + LOCK_PROGRESS_TIMEOUT_MS);
          update({ pendingPushes: pending.size, pushTimedOut: false });
          return true;
        } catch (error) {
          if (!active) return false;
          throw error;
        }
      },
      checkPushes() {
        if (!active || !snapshot) return;
        seenPushes.clear();
        trackPending(snapshot);
        update({ pendingPushes: pending.size, pushTimedOut: false });
        void tick(true);
      },
    };
    observer.current = engine;
    // Pushes can exist without a harvested lock/child row in the comparison.
    // Discover those once on entry, without polling physical providers.
    void tick(true);
    return () => {
      unsubscribe();
      stop();
      if (observer.current === engine) observer.current = null;
    };
  }, [propertyId, sessionKey, client]);

  useEffect(() => {
    if (comparison) observer.current?.comparison(comparison);
  }, [comparison, propertyId, sessionKey]);

  return {
    ...view,
    harvest: () => observer.current?.harvest() ?? Promise.resolve(false),
    harvestPhase: view.harvest,
    retryPush: (id: number) => observer.current?.retryPush(id) ?? Promise.resolve(false),
    checkPushes: () => observer.current?.checkPushes(),
  };
}